// * -------------------------------- NPM --------------------------------------
import * as React from 'react'
import ReactDOM from 'react-dom'

// * -------------------------------- MODULE --------------------------------------
import Button, { ButtonProps } from '../../../ui/components/MVButtons/Button'
import ButtonWithConfirm, { ButtonWithConfirmProps } from '../../../ui/components/MVButtons/ButtonWithConfirm'
import { ButtonVariants } from '../../../ui/components/MVButtons/types'
import { Kind } from '../../../mvtypes/kind'
import { useComponentsTranslation } from '../../../services/translation'

export type BaseButton = ButtonProps & Kind<'button'>
export type ConfirmButton = ButtonWithConfirmProps & Kind<'confirmButton'>

export type Buttons = BaseButton | ConfirmButton

export interface ModalProps {
  visible: boolean
  title: string
  onClose: () => void
  closeOnFocusOut?: boolean
  children: React.ReactNode
  width?: 25 | 30 | 40 | 50 | 75 | 'fit-content'
  leftFooterContents?: Buttons[]
  disableCloseButton?: boolean
  rightFooterContents?: Buttons[]
  additionalHeaderContent?: React.ReactNode
  id?: string
  modalBodyBackground?: 'light' | 'dark'
  modalFooterBackground?: 'light' | 'dark'
  closeButtonLabel?: string
  disableTopCloseButton?: boolean
  disableModalHeader?: boolean
  closeButtonTabIndex?: number
  verticalPosition?: 'top' | 'top-center' | 'center' | 'end'
  horizontalPosition?: 'start' | 'center' | 'end'
  formModal?: {
    onSubmit: (event: React.FormEvent<HTMLFormElement>) => void | Promise<void>
  }
}

type Props = ModalProps

interface State {
  // necessary to manage the animation of the modal
  propVisible: boolean
  visible: boolean
  isDivMounted: boolean
}

class Modal extends React.Component<Props, State> {
  // * ----------------------------------------------------------------------------------------
  // * -------------------------------- INIT --------------------------------------
  // * ----------------------------------------------------------------------------------------
  private MODAL_DIV = 'modal-container'
  // opacity transition
  private ANIMATION_SHOW_MODAL = 150

  public static defaultProps: Partial<Props> = {
    verticalPosition: 'top',
    horizontalPosition: 'center',
  }

  constructor(props: ModalProps) {
    super(props)
    this.state = {
      propVisible: props.visible,
      visible: false,
      isDivMounted: false,
    }
  }

  // * ----------------------------------------------------------------------------------------
  // * -------------------------------- STATE MANAGEMENT --------------------------------------
  // * ----------------------------------------------------------------------------------------
  public componentDidMount() {
    this.mountDivForModal()
    if (this.props.visible) {
      this.handleShowModal(this.props.visible)
    }
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (prevProps.visible !== this.props.visible) {
      if (this.props.visible) {
        this.mountDivForModal()
      }
      this.handleShowModal(this.props.visible)
    }
  }

  // * ----------------------------------------------------------------------------------------
  // * -------------------------------- BLoS --------------------------------------
  // * ----------------------------------------------------------------------------------------
  /**
   * necessary to trigger (when modal change state from invisible to visible) `display:'block` earlier than classname `mv-show`
   * and (when modal change state from visible to invisible) classname `!mv-show` earlier than `display:none`
   */
  private handleShowModal = async (isVisible: boolean) => {
    if (isVisible) {
      this.setState({ propVisible: isVisible })
      setTimeout(() => {
        this.setState({ visible: isVisible })
      }, this.ANIMATION_SHOW_MODAL)
    } else {
      this.setState({ visible: isVisible })
      setTimeout(() => {
        this.setState({ propVisible: isVisible, isDivMounted: false })
        this.props.onClose()
        this.cleanContentModal()
      }, this.ANIMATION_SHOW_MODAL)
    }
  }

  private mountDivForModal = () => {
    const div = document.getElementById(this.MODAL_DIV)
    if (!div) {
      const modalDiv = document.createElement('div')
      modalDiv.id = this.MODAL_DIV
      document.body.appendChild(modalDiv)
    }
    window.requestAnimationFrame(() => this.setState({ isDivMounted: true }))
  }

  private generateModalPosition = () => {
    return `mv-modal-dialog--${this.props.horizontalPosition}-${this.props.verticalPosition}`
  }

  private cleanContentModal = () => {
    const div = document.getElementById(this.MODAL_DIV)
    if (div) {
      div.innerHTML = ''
    }
  }

  private handleClose = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
    await this.handleShowModal(false)
  }

  private handleClickOutside = async (event: any) => {
    event.persist()
    if (event.target.className === 'modal') {
      event.preventDefault()
    }
    if (this.props.closeOnFocusOut && event.currentTarget === event.target) {
      await this.handleShowModal(false)
    }
  }
  // * ----------------------------------------------------------------------------------------
  // * -------------------------------- RENDERs --------------------------------------
  // * ----------------------------------------------------------------------------------------
  private renderModal() {
    const props = this.props
    const state = this.state

    let showCloseButton = !props.disableCloseButton
    if (showCloseButton === undefined) {
      showCloseButton = true
    }

    return (
      <div
        className={`mv-modal mv-fade mv-fade--${this.props.horizontalPosition}-${this.props.verticalPosition} ${state.visible ? `mv-show` : ''}`}
        style={{ display: state.propVisible ? 'block' : 'none' }}
        role='dialog'
        id={props.id}
        onClick={this.handleClickOutside}
      >
        <div className={`mv-modal-dialog ${state.visible ? this.generateModalPosition() : ''}  vw-${props.width || 50}`}
             role='document'>
          <div className='mv-modal-content'>
            {!props.disableModalHeader && (
              <div className='mv-modal-header'>
                <h5 className='mv-modal-title'>
                  {props.title || ''} {props.additionalHeaderContent}{' '}
                </h5>
                {!props.disableTopCloseButton && (
                  <Button
                    semantic={'secondary'}
                    variant={ButtonVariants.outline}
                    data-dismiss='modal'
                    onClick={this.handleClose}
                    icon={'times'}
                  />
                )}
              </div>
            )}
            {<div
              className={`mv-modal-body ${props.modalBodyBackground ? `bg-${props.modalBodyBackground}` : ''}`}>{props.children}</div>}
            {((props.rightFooterContents || props.leftFooterContents || showCloseButton) && (
                <div
                  className={`mv-modal-footer ${props.modalFooterBackground ? `bg-${props.modalFooterBackground}` : ''}`}>
                  <div className={'mv-modal-footer__left'}>
                    {[
                      ...((showCloseButton && [
                          <CancelButton
                            tabIndex={props.closeButtonTabIndex}
                            label={props.closeButtonLabel}
                            onClick={this.handleClose}
                            key={0.1}
                          />,
                        ]) ||
                        []),
                      ...((props.leftFooterContents &&
                          props.leftFooterContents.map((b, idx) => (
                            <ModalButton {...b} key={`additional_button_${idx}`} />
                          ))) ||
                        []),
                    ]}
                  </div>
                  <div className={'mv-modal-footer__right'}>
                    {props.rightFooterContents &&
                      props.rightFooterContents.map((b, idx) => <ModalButton {...b} key={`additional_button_${idx}`} />)}
                  </div>
                </div>
              )) ||
              null}
          </div>
        </div>
      </div>
    )
  }

  public render = () => {
    if (!this.state.isDivMounted) {
      return <></>
    }
    return ReactDOM.createPortal(
      this.props.formModal &&
      <form onSubmit={this.props.formModal.onSubmit}>{this.renderModal()}</form> ||
      this.renderModal()
      , document.getElementById(this.MODAL_DIV) as Element)
  }
}

const CancelButton = (props: {
  tabIndex?: number
  label?: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
}) => {
  const { onClick } = props
  const { t } = useComponentsTranslation()
  return (
    <ModalButton
      semantic={'secondary'}
      variant={ButtonVariants.outline}
      kind='button'
      onClick={onClick}
      label={props.label ? props.label : t(`components.modal.cancel`)}
      tabIndex={props.tabIndex}
    />
  )
}

const ModalButton = (props: Buttons) => {
  const { ...buttons } = props
  switch (buttons.kind) {
    case 'button':
      return <Button {...(buttons as ButtonProps)} />
    case 'confirmButton':
      return <ButtonWithConfirm {...(buttons as ButtonWithConfirmProps)} />
  }
}


export default Modal
